import Backbone from 'backbone';
import $ from 'jquery';

// Set up Backbone to use jQuery
Backbone.$ = $;

// Single Bubble Model for Backbone.js
const BubbleModel = Backbone.Model.extend({

  // Default attributes for a new bubble
  defaults: {
    id: null,
    name: '',
    size: 120,
    xPercent: 0.5,
    yPercent: 0.5,
    x: 0,
    y: 0,
    teamId: null,
    team: 'default',
    color: '#4CAF50',
    dx: 0,
    dy: 0
  },

  // URL root for individual bubble operations
  // Will be overridden by Collection's url when part of a collection
  urlRoot: function() {
    const backboneUrl = process.env.REACT_APP_BACKBONE_URL || 'https://ppb.chatforest.com';
    return `${backboneUrl}/api/bubbles`;
  },

  // Parse response from server (clean up any server-specific fields)
  parse: function(response) {
    // Remove any server-internal fields that shouldn't be in the model
    const cleanedResponse = { ...response };
    delete cleanedResponse.airLossTimer;
    delete cleanedResponse.movementTimer;
    delete cleanedResponse.debug; // Remove debug info from server response
    return cleanedResponse;
  },

  // Validate model data before save
  validate: function(attrs) {
    if (!attrs.name || attrs.name.trim() === '') {
      return 'Bubble name is required';
    }

    if (typeof attrs.size !== 'number' || attrs.size < 0) {
      return 'Bubble size must be a non-negative number';
    }

    if (typeof attrs.xPercent !== 'number' || attrs.xPercent < 0 || attrs.xPercent > 1) {
      return 'xPercent must be between 0 and 1';
    }

    if (typeof attrs.yPercent !== 'number' || attrs.yPercent < 0 || attrs.yPercent > 1) {
      return 'yPercent must be between 0 and 1';
    }
  },

  // Custom method to inflate bubble (increase size by 1)
  inflate: function() {
    return this.performAction('inflate');
  },

  // Custom method for god-mode inflation (+100)
  godInflate: function() {
    return this.performAction('god_inflate');
  },

  // Custom method to deflate bubble (decrease size)
  deflate: function() {
    return this.performAction('deflate');
  },

  // Custom method for god-mode deflation
  godDeflate: function() {
    return this.performAction('god_deflate');
  },

  // Custom method for god-mode pop
  godPop: function() {
    return this.performAction('god_pop');
  },

  // Real-time size calculation using deflation rate for client prediction
  getCurrentSize: function() {
    const baseSize = this.get('size');
    const lastActivityStr = this.get('last_activity');
    const deflationRate = this.get('deflation_rate') || 0.05; // Default deflation rate
    const now = new Date();

    // Handle missing last_activity - use current time (no deflation)
    if (!lastActivityStr) {
      return baseSize;
    }

    const lastActivity = new Date(lastActivityStr);
    const secondsElapsed = (now - lastActivity) / 1000;

    // Predict current size using bubble's unique deflation rate
    const predictedSize = Math.max(1, baseSize - (secondsElapsed * deflationRate));

    // Visual feedback for deflating bubbles
    if (predictedSize < baseSize * 0.8) {
      this.set('visually_deflating', true, {silent: true});
    }

    return Math.round(predictedSize);
  },

  // Override display size for real-time updates
  getDisplaySize: function() {
    return this.getCurrentSize();
  },

  // Helper method to perform actions via API
  performAction: function(action) {
    const backboneUrl = process.env.REACT_APP_BACKBONE_URL || 'https://ppb.chatforest.com';
    let endpoint;

    // Route to appropriate endpoint based on action
    if (action === 'inflate' || action === 'god_inflate') {
      endpoint = `${backboneUrl}/api/bubbles/inflate.php`;
    } else if (action === 'deflate' || action === 'god_deflate') {
      endpoint = `${backboneUrl}/api/bubbles/deflate.php`;
    } else if (action === 'god_pop') {
      endpoint = `${backboneUrl}/api/bubbles/pop.php`;
    } else {
      return Promise.reject(new Error('Invalid action'));
    }

    const self = this;

    return new Promise((resolve, reject) => {
      $.ajax({
        url: endpoint,
        method: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
          bubble_id: this.get('id'),
          action: action
        }),
        xhrFields: {
          withCredentials: false
        },
        beforeSend: function(xhr) {
          xhr.setRequestHeader('Content-Type', 'application/json');
        },
        success: function(response) {
          if (response.success && (response.action === 'popped' || response.action === 'god_pop' || response.action === 'burst')) {
            // Bubble was destroyed - remove from collection
            if (self.collection) {
              self.collection.remove(self);
            }
            self.trigger('destroy', self);
          } else {
            // Update model with server response
            self.set(self.parse(response));
          }
          resolve(response);
        },
        error: function(xhr, status, error) {
          const errorResponse = xhr.responseJSON || { error: error };
          reject(errorResponse);
        }
      });
    });
  }

});

export default BubbleModel;
